/**
 * Navigation, read ONCE at the screen root and handed to the Rail through a
 * module ref.
 *
 * UX spec §6a: nothing under the Stage or the live Rail may call
 * `useNavigation` / `useRoute` / `useContext`. `ResultCard`'s "Review" button
 * still has to reach ChessAnalysis, so the screen root stores the navigation
 * object here and the card calls `navigateTo(...)`.
 *
 * A module ref, not a context, for the same reason the store is a store: a
 * context change would re-render every consumer under the provider.
 */
type Navigation = {navigate: (route: string, params?: any) => void} | null;

let navigation: Navigation = null;

/**
 * N1 (fix N, 2026-10-06): the screen's own `route.name` — `'playChess'` or
 * `'CCTournaments'` (StackConstants L99-125, both render `PlayChessSwitch` ->
 * this screen) — kept next to the navigation object so a leaf that only ever
 * calls `navigateTo` can still learn which route it is ON, without taking
 * `useRoute()` of its own (UX spec §6a). `null` before the root's effect has
 * run, or after it unmounts.
 */
let routeName: string | null = null;

export const setNavigation = (
  next: Navigation,
  nextRouteName: string | null,
): void => {
  navigation = next;
  routeName = nextRouteName;
};

/** the route this screen is mounted under right now, or `null` if unknown. */
export const currentRoute = (): string | null => routeName;

export const navigateTo = (route: string, params?: any): boolean => {
  if (!navigation) {
    return false;
  }
  navigation.navigate(route, params);
  return true;
};

/**
 * `useSocketReconnect` is likewise read once at the root: the "Play here"
 * button of the another-tab Strip needs it, and Strip must stay context-free.
 */
let reconnect: (() => void) | null = null;

export const setReconnect = (next: (() => void) | null): void => {
  reconnect = next;
};

export const requestReconnect = (): void => {
  reconnect?.();
};
